import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { news } from "@/mocks/news";
import type { NewsItem } from "@/types";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";

const all = news as NewsItem[];
const cats = Array.from(new Set(all.map((n) => n.category)));
const PAGE_SIZE = 4;

export default function News() {
  const [active, setActive] = useState("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta(
    "影视资讯专题 - 漂亮的保姆",
    "漂亮的保姆影视资讯专题，汇集剧集新闻、演员动态、行业观察与剧情解析，帮助你及时了解影视剧最新动态。",
    "漂亮的保姆,影视资讯,剧集新闻,演员动态,行业动态,剧情解析",
  );

  const list = useMemo(
    () => (active === "全部" ? all : all.filter((n) => n.category === active)),
    [active],
  );
  const shown = list.slice(0, visible);

  return (
    <>
      <PageHeader
        title="影视资讯专题"
        desc="剧集新闻、演员动态、行业观察与剧情解析，第一时间掌握影视剧最新动态。"
        crumbs={[{ label: "首页", to: "/" }, { label: "影视资讯" }]}
      />

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          {["全部", ...cats].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setActive(c);
                setVisible(PAGE_SIZE);
              }}
              className={`px-4 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                active === c
                  ? "bg-primary-600 text-background-50"
                  : "bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {shown.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-full aspect-[16/9] overflow-hidden bg-background-200">
                    <img
                      src={n.cover}
                      alt={n.title}
                      title={`${n.title} - 漂亮的保姆`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                        {n.category}
                      </span>
                      <span className="text-xs text-foreground-400">{n.date}</span>
                    </div>
                    <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {n.title}
                    </h2>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2">
                      {n.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            {visible < list.length && (
              <div className="text-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-300 text-primary-700 bg-background-50 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg" />
                  加载更多资讯
                </button>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title="该分类暂无资讯"
            desc="换个分类看看其他影视资讯吧。"
            actionText="查看全部资讯"
            onAction={() => setActive("全部")}
          />
        )}
      </div>
    </>
  );
}